/* ═══════════════════════════════════════════════════════════════════════════
   MentionPicker — the list that opens when you type "@" in a group, on the web.

   🟥 THE SAME RULES AS THE PHONE, BECAUSE IT IS THE SAME MODULE. Which run the
      caret is in, who matches it, what gets inserted and who ends up notified all
      come from services/mentions, which is generated from the app's TypeScript by
      tools/gen-twins.mjs. A second implementation here would be a second answer to
      "is @Ali Hassan one member or two", and the two clients would disagree about
      a message they can both see.

   🟥 AND THE LIST IS LIVE FOR THE SAME REASON IT IS ON THE PHONE. `participantNames`
      and `participantImages` are written onto the chat document when somebody joins
      or the group is renamed — not when a member changes their own name or removes
      their own picture. They are the fallback; a `users/{uid}` listener per
      candidate is the answer, opened only while the picker is actually open.
   ═══════════════════════════════════════════════════════════════════════════ */
import React, { useEffect, useMemo, useState } from 'react';
import { watchUserProfile } from '../services/users';
import { cleanMentionName, MENTION_PICKER_LIMIT } from '../services/mentions';

/* 🟥 A CEILING, BECAUSE A GROUP HAS NO DOCUMENTED ONE — eight rows are shown and a
   thousand listeners to rank them is a bill nobody reads. Past this the fallback
   names still match and still insert; only the liveness stops. */
export const MENTION_LIVE_MAX = 200;

export function useLiveMentionMembers(uids, fallbackNames, fallbackPhotos, enabled) {
  const list = useMemo(
    () => (Array.isArray(uids) ? uids.filter(Boolean).slice(0, MENTION_LIVE_MAX) : []),
    [uids],
  );
  const [live, setLive] = useState({});

  useEffect(() => {
    if (!enabled || !list.length) return undefined;
    const offs = list.map((uid) => watchUserProfile(uid, (p) => {
      const name = cleanMentionName(p?.fullName || p?.username);
      const photo = p?.profileImage || '';
      setLive((prev) => {
        const was = prev[uid];
        if (was && was.name === name && was.photo === photo) return prev;
        return { ...prev, [uid]: { name, photo } };
      });
    }));
    return () => { offs.forEach((off) => { try { off(); } catch { /* already gone */ } }); };
  }, [enabled, list]);

  return useMemo(() => list.map((uid) => {
    const l = live[uid];
    return {
      uid,
      /* 🟥 the live name wins only when it EXISTS — a member whose document has not
         arrived yet must fall through to the map, or the row is blank */
      name: cleanMentionName(l?.name) || cleanMentionName(fallbackNames?.[uid]),
      photo: (l ? l.photo : fallbackPhotos?.[uid]) || '',
    };
  }), [list, live, fallbackNames, fallbackPhotos]);
}

const initialOf = (name) => {
  const n = cleanMentionName(name);
  return n ? Array.from(n)[0].toUpperCase() : '?';
};

export default function MentionPicker({ results, onPick }) {
  if (!results || !results.length) return null;
  return (
    <div className="ment-list" role="listbox">
      {results.slice(0, MENTION_PICKER_LIMIT).map((m) => (
        <button
          key={m.uid}
          type="button"
          className="ment-row"
          /* 🟥 onMouseDown, NOT onClick. A click fires after the textarea has already
             lost focus, and the insertion needs the caret that focus was holding. */
          onMouseDown={(e) => { e.preventDefault(); onPick(m); }}
        >
          {m.photo
            ? <img className="ment-av" src={m.photo} alt="" />
            : <span className="ment-av ment-av-fb">{initialOf(m.name)}</span>}
          {/* 🟥 THE NAME, AND ONLY THE NAME — a handle under it is the one thing
              this list was explicitly asked not to show */}
          <span className="ment-name">{m.name}</span>
        </button>
      ))}
    </div>
  );
}
